<script setup lang="ts">
import { THEME } from '@pkg/config/misc/theme';
import { cn } from '@pkg/ui/lib/utils';
import { useElementVisibility } from '@vueuse/core';
import { BagVariant } from '@pkg/sanity-utils/types/bag';

interface Props {
  variant: BagVariant;
  title: {
    preTitle?: string;
    title?: string;
  };
}
defineProps<Props>();

const BagImages = {
  [BagVariant.FRUITS]: '/images/bag.fruits.png',
  [BagVariant.BREAD]: '/images/bag.bread.png',
};

const target = ref(null);
const targetIsCurrentlyVisible = useElementVisibility(target);

const targetHasBeenVisible = ref(false);

watch(targetIsCurrentlyVisible, (value) => {
  if (value) {
    targetHasBeenVisible.value = true;
  }
});

</script>

<template>
  <PartialSection
    :theme="THEME.LIGHT"
    :class="
      cn('c-bag__section', {
        'animated--not-started': !targetHasBeenVisible,
      })
    "
  >
    <BaseTitle
      class="c-bag__title animated animated__title"
      :title="title.title"
      :pre-title="title.preTitle"
      variant="center"
      size="bag"
    />
    <div class="c-bag__bag-parent">
      <div class="c-bag__bag animated animated__bag">
        <LocalImage
          ref="target"
          class="c-bag__img"
          :src="BagImages[variant]"
          :quality="70"
          :width="2910"
          :height="2100"
          format="webp"
          loading="lazy"
        />
      </div>
      <div class="c-bag__content w-full flex flex-row justify-between wrapper">
        <div class="animated animated__a c-bag__block c-bag__block--left">
          <slot name="upperLeftBlock" />
        </div>
        <div class="animated animated__c c-bag__block c-bag__block--right">
          <slot name="upperRightBlock" />
        </div>
        <div class="animated animated__b c-bag__block c-bag__block--left">
          <slot name="lowerLeftBlock" />
        </div>
        <div class="animated animated__d c-bag__block c-bag__block--right">
          <slot name="lowerRightBlock" />
        </div>
      </div>
    </div>
  </PartialSection>
</template>

<style scoped>
@import '~/assets/css/mixins/_mixins.index.pcss';

.c-bag {
  &__section {
    padding-bottom: 0;
    overflow-y: hidden;
    display: flex;
    flex-direction: column;
  }

  &__bag-parent {
    position: relative;
    display: flex;
    flex-direction: column-reverse;
    gap: .5rem;

    @screen desk {
      display: grid;
      grid-template-areas: "stack";
    }

    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 100vw;
  }

  &__title {
    grid-area: stack;
    z-index: 0;
    margin-bottom: 1.5rem;
  }

  &__bag {
    position: relative;
    overflow: hidden;
    width: 100%;
    z-index: 1;

    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;

    @screen desk {
      inset: 0;
      grid-area: stack;
    }
  }

  &__img {
    object-fit: cover;
    object-position: top;
    --image-width: 533px;

    min-width: var(--image-width);
    max-width: var(--image-width);

    margin-bottom: -14rem;

    @screen tablet-wide {
      --image-width: 777px;
      margin-bottom: -7rem;
    }

    @screen desk {
      --image-width: 1020px;
      margin-bottom: -9rem;
    }

  }

  &__content {
    position: relative;
    gap: 16px;
    flex: 1;
    z-index: 0;

    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: min-content;
    column-gap: 1rem;
    row-gap: 1.5rem;
    align-content: center;

    @screen desk {
      position: absolute;
      inset: 0;
      grid-area: stack;
      grid-column: full;

      grid-template-columns: 1fr 637px 1fr;
      row-gap: 3.5rem;
    }
  }

  &__block {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    color: var(--theme-heading-color);
    max-width: 143px;

    @screen tablet-wide {
      max-width: 282px;
    }

    &--left {
      grid-column-start: 1;
      align-items: flex-end;
      justify-self: end;

      :deep(.c-base-title__header) {
        text-align: right;
      }
    }

    &--right {
      grid-column-end: -1;
      align-items: flex-start;
      justify-self: start;
      text-align: left;

      :deep(.c-base-title__header) {
        text-align: left;
      }
    }
  }

}

.animated--not-started .animated {
  animation-play-state: paused;
}

.animated {
  --animation-speed: 1.5s;
  --animation-distance: 150px;

  animation: scrollUp var(--animation-speed) ease-in-out;
  animation-fill-mode: both;
  animation-play-state: running;

  &__bag {
    --animation-distance: 100px;
    @screen desk {
      --animation-distance: 200px;
    }
  }

  &__title {
    --animation-distance: 350px;
    @screen desk {
      --animation-distance: 350px;
    }
  }

  &__a {
    --animation-distance: 375px;
  }

  &__c {
    --animation-distance: 400px;
  }

  &__b {
    --animation-distance: 425px;
  }

  &__d {
    --animation-distance: 450px;
  }

  &__a,
  &__c {
    @screen desk {
      --animation-distance: 300px;
    }
  }

  &__b,
  &__d {
    @screen desk {
      --animation-distance: 400px;
    }
  }
}

@keyframes scrollUp {
  0% {
    transform: translateY(var(--animation-distance));
  }
  100% {
    transform: translateY(0);
  }
}
</style>
